'use client';

import { motion } from 'framer-motion';
import { timeline, scientists, atomicModels, categoryColors, categoryNames } from '@/data/atomHistory';
import { TimelineItem } from '@/components/TimelineItem';
import { ScientistCard } from '@/components/ScientistCard';
import { AtomicModelCard } from '@/components/AtomicModelCard';
import { Card, CardContent } from '@/components/ui/card';
import { ExternalLink, BookOpen, Atom, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTranslations } from 'next-intl';

export default function HistoryPage() {
  const t = useTranslations('History');

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="inline-block mb-6"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#64ffda] to-[#82aaff] flex items-center justify-center">
            <Atom className="w-10 h-10 text-[#0a192f]" />
          </div>
        </motion.div>

        <h1 className="text-5xl font-bold dark:text-white light:text-gray-900 mb-4">
          {t('title')}
        </h1>

        <p className="text-xl dark:text-[#a8b2d1] light:text-gray-600 max-w-3xl mx-auto leading-relaxed">
          {t('subtitle')}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="mb-16"
      >
        <Card className="dark:bg-gradient-to-br dark:from-[#112240] dark:via-[#1a2744] dark:to-[#112240] light:from-white light:to-gray-50 dark:border-[#233554] light:border-gray-200 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-gradient-to-br from-[#64ffda] to-transparent" />
          </div>
          <CardContent className="relative p-8">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 dark:text-[#64ffda] light:text-teal-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold dark:text-white light:text-gray-900 mb-3">
                  {t('introTitle')}
                </h2>
                <p className="dark:text-[#ccd6f6] light:text-gray-700 text-lg leading-relaxed">
                  {t('introText')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mb-12"
      >
        <div className="flex flex-wrap justify-center gap-4">
          {Object.entries(categoryColors).map(([key, color]) => (
            <Badge
              key={key}
              variant="outline"
              className="px-4 py-2"
              style={{
                borderColor: color,
                color: color,
                backgroundColor: `${color}10`
              }}
            >
              {categoryNames[key as keyof typeof categoryNames]}
            </Badge>
          ))}
        </div>
      </motion.div>

      <section className="mb-20">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Clock className="w-8 h-8 dark:text-[#64ffda] light:text-teal-600" />
            <h2 className="text-4xl font-bold dark:text-white light:text-gray-900">
              {t('timelineTitle')}
            </h2>
          </div>
          <p className="dark:text-[#a8b2d1] light:text-gray-600 text-lg">
            {t('timelineSubtitle')}
          </p>
        </motion.div>

        <div className="relative">
          <div className="absolute right-[18px] md:left-1/2 top-0 bottom-0 w-0.5 dark:bg-[#233554] light:bg-gray-300 md:-translate-x-1/2" />
          <div className="space-y-8 md:space-y-12">
            {timeline.map((event, index) => (
              <TimelineItem key={index} event={event} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="mb-20">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Atom className="w-8 h-8 dark:text-[#64ffda] light:text-teal-600" />
            <h2 className="text-4xl font-bold dark:text-white light:text-gray-900">
              {t('modelsTitle')}
            </h2>
          </div>
          <p className="dark:text-[#a8b2d1] light:text-gray-600 text-lg max-w-2xl mx-auto">
            {t('modelsSubtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {atomicModels.map((model, index) => (
            <AtomicModelCard key={index} model={model} index={index} />
          ))}
        </div>
      </section>

      <section className="mb-20">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold dark:text-white light:text-gray-900 mb-4">
            {t('scientistsTitle')}
          </h2>
          <p className="dark:text-[#a8b2d1] light:text-gray-600 text-lg max-w-2xl mx-auto">
            {t('scientistsSubtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {scientists.map((scientist, index) => (
            <ScientistCard key={index} scientist={scientist} index={index} />
          ))}
        </div>
      </section>

      <section>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold dark:text-white light:text-gray-900 mb-4">
            {t('resourcesTitle')}
          </h2>
          <p className="dark:text-[#a8b2d1] light:text-gray-600 text-lg">
            {t('resourcesSubtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { name: 'CERN', descKey: 'cernDesc' as const, url: 'https://home.cern/', color: '#64ffda' },
            { name: 'CNRS', descKey: 'cnrsDesc' as const, url: 'https://www.cnrs.fr/', color: '#82aaff' },
            { name: 'Khan Academy', descKey: 'khanDesc' as const, url: 'https://www.khanacademy.org/science/chemistry', color: '#f78c6c' },
          ].map((resource, index) => (
            <motion.a
              key={index}
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <Card className="h-full dark:bg-[#112240] light:bg-white dark:border-[#233554] light:border-gray-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold dark:text-white light:text-gray-900">
                      {resource.name}
                    </h3>
                    <ExternalLink className="w-5 h-5 dark:text-[#64ffda] light:text-teal-600" />
                  </div>
                  <p className="dark:text-[#a8b2d1] light:text-gray-600">
                    {t(resource.descKey)}
                  </p>
                </CardContent>
              </Card>
            </motion.a>
          ))}
        </div>
      </section>
    </div>
  );
}
